import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

function s3AssetUrl(base, filename) {
  if (!base || !filename) return "";
  return base + encodeURIComponent(String(filename)).replace(/%2F/g, "/");
}

function toSlugKey(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function formatKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function clampFontSize(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 20;
  return Math.min(40, Math.max(12, Math.round(num)));
}

function CategoryImage({ src, alt }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <div className="rules-category-image rules-category-image-placeholder">No image</div>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className="rules-category-image"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);
  const [colors, setColors] = useState({
    text_color: "#000000",
    button_text_color: "#c8e1f4",
    button_background_color: "#034b82",
  });
  const [fontSize, setFontSize] = useState(20);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    const nestedColors =
      data?.colors && typeof data.colors === "object" && !Array.isArray(data.colors)
        ? data.colors
        : null;
    const source = nestedColors || data;
    if (!source) return;

    setColors((prev) => ({
      text_color: source.text_color ?? prev.text_color,
      button_text_color:
        source.button_text_color ?? source.button_color ?? prev.button_text_color,
      button_background_color:
        source.button_background_color ??
        source.button_backgroundcolor ??
        prev.button_background_color,
    }));

    if (source.font_size != null) {
      setFontSize(clampFontSize(source.font_size));
    }
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async () => {
    const payload = {
      data: {
        text_color: colors.text_color,
        button_text_color: colors.button_text_color,
        button_background_color: colors.button_background_color,
        font_size: clampFontSize(fontSize),
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      setTimeout(() => navigate("/admin/customize-spot"), 500);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    }
  };

  const S3 = process.env.REACT_APP_S3_PATH || "";
  const rawCategories = Array.isArray(data?.categories) ? data.categories : [];
  const categoryMeta =
    data?.category_meta && typeof data.category_meta === "object" && !Array.isArray(data.category_meta)
      ? data.category_meta
      : {};
  const categoryQuestions =
    data?.category_images_names &&
    typeof data.category_images_names === "object" &&
    !Array.isArray(data.category_images_names)
      ? data.category_images_names
      : {};

  const categoryCards = [];
  const seen = new Set();

  rawCategories.forEach((displayName) => {
    const key = toSlugKey(displayName);
    const meta = categoryMeta[key] || {};
    categoryCards.push({
      key,
      label: meta.label || displayName,
      image: meta.image || "",
      questionCount: Array.isArray(categoryQuestions[key]) ? categoryQuestions[key].length : 0,
    });
    seen.add(key);
  });

  Object.keys(categoryMeta).forEach((key) => {
    if (seen.has(key)) return;
    const meta = categoryMeta[key] || {};
    categoryCards.push({
      key,
      label: meta.label || formatKey(key),
      image: meta.image || "",
      questionCount: Array.isArray(categoryQuestions[key]) ? categoryQuestions[key].length : 0,
    });
    seen.add(key);
  });

  Object.keys(categoryQuestions).forEach((key) => {
    if (seen.has(key)) return;
    categoryCards.push({
      key,
      label: formatKey(key),
      image: "",
      questionCount: Array.isArray(categoryQuestions[key]) ? categoryQuestions[key].length : 0,
    });
    seen.add(key);
  });

  const previewTitleSize = `${Math.max(fontSize + 10, 24)}px`;
  const previewTextSize = `${fontSize}px`;
  const previewButtonSize = `${Math.max(fontSize - 2, 12)}px`;

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="rules-section-head">
                  <div className="admin-tab-title">
                    <i className="fa-solid fa-layer-group"></i> &nbsp; Categories
                  </div>
                  <button
                    type="button"
                    className="rules-manage-btn"
                    onClick={() => navigate("/admin/customize-spot")}
                  >
                    Manage Categories
                  </button>
                </div>
                {categoryCards.length === 0 ? (
                  <div className="rules-empty-state">No categories configured for this theme.</div>
                ) : (
                  <div className="rules-category-grid">
                    {categoryCards.map((category) => (
                      <div className="rules-category-card" key={category.key}>
                        <CategoryImage
                          src={s3AssetUrl(S3, category.image)}
                          alt={category.label}
                        />
                        <div className="rules-category-name">{category.label}</div>
                        {/* <div className="code">
                          Image: {category.image || "Not set"}
                          <br />
                          Questions: <strong>{category.questionCount}</strong>
                        </div> */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container rules-settings-card">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
                </div>
                <p className="rules-settings-intro">
                  Adjust the core theme colors and text size, then preview the result before saving.
                </p>

                <div className="rules-preview-panel">
                  <div
                    className="rules-preview-title"
                    style={{ color: colors.text_color, fontSize: previewTitleSize }}
                  >
                    Cubicle Chaos
                  </div>
                  <div
                    className="rules-preview-text"
                    style={{ color: colors.text_color, fontSize: previewTextSize }}
                  >
                    Live preview of your current theme settings.
                  </div>
                  <button
                    type="button"
                    className="rules-preview-button"
                    style={{
                      color: colors.button_text_color,
                      backgroundColor: colors.button_background_color,
                      fontSize: previewButtonSize,
                    }}
                  >
                    Sample Button
                  </button>
                </div>

                <div className="rules-settings-list">
                  <div className="rules-setting-row">
                    <div className="rules-setting-copy">
                      <label className="rules-setting-label">Text Color</label>
                      <div className="rules-setting-help">Used for main labels and headings.</div>
                    </div>
                    <div className="rules-setting-control">
                      <input
                        type="color"
                        className="rules-color-input"
                        value={colors.text_color}
                        onChange={handleColorChange("text_color")}
                        aria-label="Text color"
                      />
                      <span className="rules-color-value">{colors.text_color}</span>
                    </div>
                  </div>

                  <div className="rules-setting-row">
                    <div className="rules-setting-copy">
                      <label className="rules-setting-label">Button Text Color</label>
                      <div className="rules-setting-help">Text color shown on buttons.</div>
                    </div>
                    <div className="rules-setting-control">
                      <input
                        type="color"
                        className="rules-color-input"
                        value={colors.button_text_color}
                        onChange={handleColorChange("button_text_color")}
                        aria-label="Button text color"
                      />
                      <span className="rules-color-value">{colors.button_text_color}</span>
                    </div>
                  </div>

                  <div className="rules-setting-row">
                    <div className="rules-setting-copy">
                      <label className="rules-setting-label">Button Background Color</label>
                      <div className="rules-setting-help">Background fill used on buttons.</div>
                    </div>
                    <div className="rules-setting-control">
                      <input
                        type="color"
                        className="rules-color-input"
                        value={colors.button_background_color}
                        onChange={handleColorChange("button_background_color")}
                        aria-label="Button background color"
                      />
                      <span className="rules-color-value">{colors.button_background_color}</span>
                    </div>
                  </div>

                  <div className="rules-setting-row rules-setting-row-font">
                    <div className="rules-setting-copy">
                      <label className="rules-setting-label">Font Size</label>
                      <div className="rules-setting-help">Controls the size of theme text.</div>
                    </div>
                    <div className="rules-font-controls">
                      <input
                        type="range"
                        min="12"
                        max="40"
                        value={fontSize}
                        onChange={(e) => setFontSize(clampFontSize(e.target.value))}
                        className="rules-font-range"
                        aria-label="Font size"
                      />
                      <input
                        type="number"
                        min="12"
                        max="40"
                        value={fontSize}
                        onChange={(e) => setFontSize(clampFontSize(e.target.value))}
                        className="rules-font-number"
                        aria-label="Font size number"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
